import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular/lazy';
import { StorageService } from 'src/app/services/storage.service';
import { ObserverService } from 'src/app/services/observer.service';
import { DatabaseService } from 'src/app/services/database.service';
import { ConnectedAccountService } from 'src/app/services/connected-account.service';
import { APIService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { Router } from '@angular/router';
import moment from 'moment-timezone';
import { Decimal } from 'decimal.js';

@Injectable()
export class CashTrackingService {

    moment = moment;

    constructor(
        public alertController: AlertController,
        private observerService: ObserverService,
        private storageService: StorageService,
        private databaseService: DatabaseService,
        private connectedAccountService: ConnectedAccountService,
        private apiService: APIService,
        private authService: AuthService,
        private router: Router
    ) { }

    getCashTrackingReport(cash_tracking_report, format, paper_size) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getCashTrackingReport(cash_tracking_report, format, paper_size).then((report) => {
                    resolve(report);
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status == 400) {
                        this.presentAlert("Unknown error", "Unfortunately something went wrong while trying to generate the cash tracking report.");
                    }
                    else if(error.status == 403) {
                        this.presentAlert("Insufficient permissions", "You don't have permission to generate a cash tracking report.");
                    }
                    else if(error.status == 0) {
                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ['OK'],
        });
        await alert.present();
    }

    addCashMovement(raw_cash_movement) {
        return new Promise<void>((resolve, reject) => {
            this.storageService.get("connected_account_id").then((connected_account_id) => {
                if(connected_account_id) {
                    this.connectedAccountService.getConnectedAccount(connected_account_id).then((connected_account) => {
                        if(connected_account) {
                            let cash_movement = {};
                            cash_movement["user_id"] = connected_account['user_id'];
                            cash_movement["price"] = raw_cash_movement.price;
                            cash_movement["currency_id"] = raw_cash_movement.currency_id;
                            cash_movement['date'] = new Date();
                            if(raw_cash_movement['note']) {
                                cash_movement['note'] = raw_cash_movement['note'];
                            }
                            this.databaseService.cash_movement.add(cash_movement).then(() => {
                                this.observerService.publish("cash_movement_added", cash_movement);
                                resolve();
                            })
                            .catch(() => {
                                reject();
                            });
                        }
                        else {
                            reject();
                        }
                    })
                    .catch(() => {
                        reject();
                    });
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    addCashTracking(cash_tracking_session_floats) {
        return new Promise((resolve, reject) => {
            if(cash_tracking_session_floats) {
                this.storageService.get("connected_account_id").then((connected_account_id) => {
                    if(connected_account_id) {
                        this.connectedAccountService.getConnectedAccount(connected_account_id).then((connected_account) => {
                            if(connected_account) {
                                let cash_tracking_session = {};
                                cash_tracking_session["floats"] = cash_tracking_session_floats;
                                cash_tracking_session["user_id"] = connected_account['user_id'];
                                cash_tracking_session["start_date"] = new Date();
                                this.storageService.set("cash_tracking_session", cash_tracking_session).then(() => {
                                    this.observerService.publish("cash_tracking_session_changed", cash_tracking_session);
                                    resolve(cash_tracking_session);
                                })
                                .catch(() => {
                                    reject();
                                });

                            }
                            else {
                                reject();
                            }
                        })
                        .catch(() => {
                            reject();
                        });
                    }
                    else {
                        reject();
                    }
                })
                .catch(() => {
                    reject();
                });
            }
            else {
                reject();
            }
        });
    }

    deleteCashTracking() {
        return new Promise<void>((resolve, reject) => {
            this.storageService.remove('cash_tracking_session').then(() => {
                this.databaseService.cash_movement.clear().then(() => {
                    this.observerService.publish("cash_tracking_session_deleted", (null));
                    resolve();
                })
                    .catch(() => {
                        reject();
                    });
            })
                .catch(() => {
                    reject();
                });
        });
    }

    getCashMovements() {
        return new Promise((resolve, reject) => {
            this.databaseService.cash_movement.toArray().then((cash_movements) => {
                resolve(cash_movements);
            })
            .catch(() => {
                reject();
            });
        });
    }

    getCashTrackingSession() {
        return new Promise((resolve, reject) => {
            this.storageService.get("cash_tracking_session").then((cash_tracking_session) => {
                if (cash_tracking_session) {
                    resolve(cash_tracking_session);
                }
                else {
                    resolve(null)
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

}
